import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { RealEstateAgentSearchableDropdownComponent } from '@smooved-lib/lib/real-estate-agent-searchable-dropdown/real-estate-agent-searchable-dropdown.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Move } from '@app/move/interfaces/move';
import { DbUtils, SimpleChangesUtils } from '@core/utils';
import { RealEstateOfficeSearchableDropdownComponent } from '@smooved-lib/lib/real-estate-office-searchable-dropdown/real-estate-office-searchable-dropdown.component';
import { RealEstateGroupSearchableDropdownComponent } from '@smooved-lib/lib/real-estate-group-searchable-dropdown/real-estate-group-searchable-dropdown.component';
import { RxjsComponent } from '@core/rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MoveService } from '@app/move/services/move.service';
import { ButtonAppearance, ButtonModule, ModalSandbox, NotificationSandbox, UiContext, UiIconSize } from '@smooved/ui';
import { DeleteMoveModal } from '@app/admin/modals/delete-move/delete-move.modal';

@Component({
    selector: 'app-move-edit-danger-zone',
    standalone: true,
    imports: [
        RealEstateAgentSearchableDropdownComponent,
        ReactiveFormsModule,
        RealEstateOfficeSearchableDropdownComponent,
        RealEstateGroupSearchableDropdownComponent,
        TranslateModule,
        ButtonModule,
    ],
    template: `
        <div class="u-grid u-grid-col-2 u-grid-gap">
            <form [formGroup]="form" (submit)="onSubmit()" class="u-bordered-lightest-right">
                <h5 class="u-margin-bottom-half">{{ 'MOVE.LINK_REAL_ESTATE.TITLE' | translate }}</h5>
                <lib-real-estate-group-searchable-dropdown
                    formControlName="realEstateGroup"
                    class="u-display-block u-margin-bottom"
                ></lib-real-estate-group-searchable-dropdown>
                @if (includeRealEstateOffice) {
                    <lib-real-estate-office-searchable-dropdown
                        class="u-display-block u-margin-bottom"
                        formControlName="realEstateOffice"
                        [realEstateGroupId]="form.get('realEstateGroup').value"
                    ></lib-real-estate-office-searchable-dropdown>
                }
                <lib-real-estate-agent-searchable-dropdown
                    [realEstateGroupId]="form.get('realEstateGroup').value"
                    formControlName="realEstateAgent"
                    class="u-display-block u-margin-bottom"
                ></lib-real-estate-agent-searchable-dropdown>
                <div>
                    <button [context]="uiContext.Secondary" smvd-ui-button type="submit">{{ 'COMMON.SUBMIT' | translate }}</button>
                </div>
            </form>

            <div>
                <h5 class="u-margin-bottom-half">{{ 'MOVE.DELETE.TITLE' | translate }}</h5>
                <button smvd-ui-button [context]="uiContext.Warning" [title]="'COMMON.DELETE' | translate" (click)="openDeleteModal(move)">
                    {{ 'COMMON.DELETE' | translate }}
                </button>
            </div>
        </div>
    `,
})
export class MoveEditDangerZoneComponent extends RxjsComponent implements OnChanges, OnInit {
    @Input() public move: Pick<Move, 'realEstateGroup' | 'realEstateAgent'>;
    @Input() public includeRealEstateOffice = false;

    @Output() public moveDeleted = new EventEmitter<Move>();

    private readonly moveService = inject(MoveService);
    private readonly notificationSandbox = inject(NotificationSandbox);
    private readonly modalSandbox = inject(ModalSandbox);

    public form: FormGroup = new FormGroup({
        realEstateGroup: new FormControl(null),
        realEstateAgent: new FormControl(null),
    });

    public ngOnInit(): void {
        if (this.includeRealEstateOffice) {
            this.form.addControl('realEstateOffice', new FormControl(null));
        }
    }

    public openDeleteModal(move: Move): void {
        this.modalSandbox.openModal(null, null, null, DeleteMoveModal, { data: move }, (deleted) => {
            if (!deleted) return;
            this.moveDeleted.emit();
        });
    }

    public ngOnChanges({ move }: SimpleChanges): void {
        if (SimpleChangesUtils.hasChanged(move)) {
            this.form.patchValue({
                realEstateGroup: DbUtils.getStringId(this.move.realEstateGroup),
                realEstateAgent: DbUtils.getStringId(this.move.realEstateAgent),
            });
        }
    }

    public onSubmit(): void {
        if (this.form.invalid || !DbUtils.getStringId(this.move)) return;
        this.moveService.linkRealEstate(DbUtils.getStringId(this.move), this.form.value).subscribe(() => {
            this.notificationSandbox.updatedSuccess();
        });
    }

    protected readonly buttonAppearance = ButtonAppearance;
    protected readonly uiContext = UiContext;
    protected readonly iconSize = UiIconSize;
}
